import React from 'react';
import { Box, Card, CardActionArea, CardContent, Chip, Grid, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import type { Features } from '../EnvironmentContext';
import { useEnvironment } from '../EnvironmentContext';
import { isFeatureEnabled, type FeatureFlagMap, type FeatureKey } from '../featureFlags';
import type { TeamAccess } from '../TeamAccessContext';
import { isAccessibilityAuditor } from '../accessibilityAudit';

export interface ToolTile {
  label: string;
  description: string;
  path: string;
  icon: React.ReactNode;
  requireRole?: (roles: string[]) => boolean;
  /**
   * Optional dynamic access gate evaluated against the full tile context (roles AND flags). Use
   * this — rather than {@link requireRole} — when visibility depends on a feature flag that can be
   * flipped per environment, e.g. a tool that is open to everyone until a "restrict" flag is turned
   * on, after which only a dedicated group role keeps it. Returning true shows the tile.
   */
  requireAccess?: (ctx: TileContext) => boolean;
  /**
   * Optional feature-flag gate, named by catalog key. Some routes are only registered when their
   * flag is on, so a tile that ignored the flag would link straight to the not-found fallback.
   */
  requireFeature?: FeatureKey;
  /** Optional team-membership gate for surfaces reached through per-team capability checks. */
  requireTeamAccess?: (access: TeamAccess) => boolean;
  /**
   * Optional "Preview" badge gate. Marks a tile whose surface is reachable but not yet trustworthy
   * — shipped ahead of the backing data or integration. Visibility is unaffected; this only badges.
   */
  isPreview?: (features: Features) => boolean;
}

export interface ToolGroup {
  label: string;
  tiles: ToolTile[];
}

/** Everything a tile gate can be decided from. */
export interface TileContext {
  roles: string[];
  features: Features;
  flags: FeatureFlagMap;
  teamAccess?: TeamAccess;
}

const canSee = (tile: ToolTile, ctx: TileContext) =>
  (isAccessibilityAuditor(ctx.roles) || !tile.requireRole || tile.requireRole(ctx.roles)) &&
  (isAccessibilityAuditor(ctx.roles) || !tile.requireAccess || tile.requireAccess(ctx)) &&
  (!tile.requireFeature || isFeatureEnabled(ctx.flags, tile.requireFeature)) &&
  (isAccessibilityAuditor(ctx.roles) ||
    !tile.requireTeamAccess ||
    (ctx.teamAccess ? tile.requireTeamAccess(ctx.teamAccess) : false));

const byLabel = <T extends { label: string }>(a: T, b: T) => a.label.localeCompare(b.label);

/** Tiles this user may open, in label order. */
export const visibleTiles = (tiles: ToolTile[], ctx: TileContext): ToolTile[] =>
  tiles.filter((t) => canSee(t, ctx)).sort(byLabel);

/**
 * Groups with at least one visible tile, groups and tiles both in label order. Sorting here rather
 * than in the catalog means a tile added later lands in the right place without anyone remembering.
 */
export const visibleGroups = (groups: ToolGroup[], ctx: TileContext): ToolGroup[] =>
  groups
    .map((g) => ({ ...g, tiles: visibleTiles(g.tiles, ctx) }))
    .filter((g) => g.tiles.length > 0)
    .sort(byLabel);

/**
 * True when at least one tile is visible. Nav entries that lead to a tile page use this so the
 * entry and the page it opens can never disagree about whether there is anything to show.
 */
export const canSeeAnyTile = (tiles: ToolTile[], ctx: TileContext): boolean =>
  tiles.some((t) => canSee(t, ctx));

const ToolTileGrid: React.FC<{ tiles: ToolTile[] }> = ({ tiles }) => {
  const navigate = useNavigate();
  const { features } = useEnvironment();

  return (
    <Grid container spacing={2}>
      {tiles.map((tool) => (
        <Grid size={{ xs: 12, sm: 6, md: 4 }} key={tool.path}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardActionArea
              onClick={() => navigate(tool.path)}
              sx={{
                p: 2,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                justifyContent: 'flex-start',
              }}
            >
              <CardContent sx={{ p: 0 }}>
                <Box sx={{ color: 'primary.main', mb: 1.5 }}>{tool.icon}</Box>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  {tool.label}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {tool.description}
                </Typography>
                {tool.isPreview?.(features) && (
                  <Chip label="Preview" size="small" color="info" variant="outlined" sx={{ mt: 1 }} />
                )}
              </CardContent>
            </CardActionArea>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
};

export default ToolTileGrid;
